import React, { useRef, useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface FlowingSphereProps {
  size?: number;
  className?: string;
  isFlowing?: boolean;
}

export const FlowingSphere: React.FC<FlowingSphereProps> = ({
  size = 24,
  className = '',
  isFlowing = true,
}) => {
  const s = size;
  const glowIntensity = isFlowing ? 1 : 0.4;
  const [isVisible, setIsVisible] = useState(true);
  const [glowPos, setGlowPos] = useState<{ x: number; y: number } | null>(null);
  const sphereRef = useRef<HTMLDivElement>(null);

  // 计算光球在视口中的位置，供 fixed 定位的光晕使用
  const updatePos = useCallback(() => {
    const el = sphereRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setGlowPos({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
  }, []);

  useEffect(() => {
    const el = sphereRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    updatePos();
    // 滚动/resize 时更新位置
    const onScroll = () => updatePos();
    const onResize = () => updatePos();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    // MutationObserver 监听 DOM 变化（如面板动画）
    const mo = new MutationObserver(() => updatePos());
    if (sphereRef.current) {
      mo.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    }
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      mo.disconnect();
    };
  }, [updatePos]);

  const shouldAnimate = isFlowing && isVisible;

  // 光晕层通过 Portal 渲染到 body，用 fixed 定位，不受任何父容器 overflow 裁剪
  const glowLayers = glowPos ? (
    <>
      {/* 外层扩散光晕 - 呼吸效果 */}
      <div
        style={{
          position: 'fixed',
          left: glowPos.x - s * (0.5 + 0.6),
          top: glowPos.y - s * (0.5 + 0.6),
          width: s * (1 + 1.2),
          height: s * (1 + 1.2),
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.35) 0%, rgba(147, 51, 234, 0.2) 30%, rgba(59, 130, 246, 0.1) 50%, transparent 70%)',
          opacity: glowIntensity,
          transition: 'opacity 1.2s ease',
          animation: shouldAnimate ? 'sphereGlowBreathe 3s ease-in-out infinite' : 'none',
          filter: `blur(${s * 0.2}px)`,
          pointerEvents: 'none',
          zIndex: 39,
        }}
      />

      {/* 中层光晕 - 柔和环境光 */}
      <div
        style={{
          position: 'fixed',
          left: glowPos.x - s * (0.5 + 0.25),
          top: glowPos.y - s * (0.5 + 0.25),
          width: s * (1 + 0.5),
          height: s * (1 + 0.5),
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0, 212, 255, 0.2) 0%, rgba(147, 51, 234, 0.12) 40%, transparent 65%)',
          opacity: isFlowing ? 0.8 : 0.3,
          transition: 'opacity 1s ease',
          filter: `blur(${s * 0.08}px)`,
          pointerEvents: 'none',
          zIndex: 39,
        }}
      />

      {/* 脉冲光环 */}
      <div
        style={{
          position: 'fixed',
          left: glowPos.x - s * 0.5 - 3,
          top: glowPos.y - s * 0.5 - 3,
          width: s + 6,
          height: s + 6,
          borderRadius: '50%',
          border: '1.5px solid rgba(0, 212, 255, 0.2)',
          animation: shouldAnimate ? 'sphereRingPulse 2.5s ease-in-out infinite' : 'none',
          opacity: isFlowing ? 1 : 0.25,
          transition: 'opacity 0.8s ease',
          pointerEvents: 'none',
          zIndex: 39,
        }}
      />
    </>
  ) : null;

  return (
    <>
      {createPortal(glowLayers, document.body)}
      <div
        ref={sphereRef}
        className={`flowing-sphere-wrapper ${className}`}
        style={{
          width: s,
          height: s,
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          willChange: shouldAnimate ? 'transform, opacity' : undefined,
        }}
      >
        {/* 主球体 */}
        <div
          style={{
            width: s,
            height: s,
            borderRadius: '50%',
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(145deg, #0f1729 0%, #1a1f3a 40%, #0d1221 100%)',
            boxShadow: `
              0 0 ${s * 0.3}px rgba(0, 212, 255, ${isFlowing ? 0.25 : 0.08}),
              0 0 ${s * 0.8}px rgba(147, 51, 234, ${isFlowing ? 0.15 : 0.04}),
              0 0 ${s * 1.5}px rgba(59, 130, 246, ${isFlowing ? 0.08 : 0.02}),
              inset 0 1px 2px rgba(255, 255, 255, 0.12),
              inset 0 -2px 4px rgba(0, 0, 0, 0.5),
              inset 2px 2px 6px rgba(255, 255, 255, 0.06)
            `,
            transition: 'box-shadow 1s ease',
          }}
        >
          {/* 内部流动光带 - 多层叠加 */}
          <div
            style={{
              position: 'absolute',
              inset: -s,
              borderRadius: '42%',
              background: 'conic-gradient(from 0deg, transparent 0%, rgba(0, 212, 255, 0.55) 12%, rgba(147, 51, 234, 0.45) 28%, transparent 42%, rgba(59, 130, 246, 0.5) 58%, rgba(236, 72, 153, 0.35) 72%, transparent 88%)',
              animation: 'sphereRotate1 3.5s linear infinite',
              animationPlayState: shouldAnimate ? 'running' : 'paused',
              filter: 'blur(2px)',
              opacity: 0.9,
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: -s * 0.55,
              borderRadius: '46%',
              background: 'conic-gradient(from 180deg, transparent 0%, rgba(59, 130, 246, 0.45) 18%, rgba(0, 212, 255, 0.4) 38%, transparent 52%, rgba(147, 51, 234, 0.45) 68%, rgba(236, 72, 153, 0.3) 82%, transparent 100%)',
              animation: 'sphereRotate2 5s linear infinite reverse',
              animationPlayState: shouldAnimate ? 'running' : 'paused',
              filter: 'blur(1.5px)',
              opacity: 0.85,
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: -s * 0.25,
              borderRadius: '48%',
              background: 'conic-gradient(from 90deg, transparent 0%, rgba(236, 72, 153, 0.35) 22%, rgba(0, 212, 255, 0.45) 48%, transparent 62%, rgba(147, 51, 234, 0.35) 78%, transparent 100%)',
              animation: 'sphereRotate3 2.8s linear infinite',
              animationPlayState: shouldAnimate ? 'running' : 'paused',
              filter: 'blur(1px)',
              opacity: 0.8,
            }}
          />

          {/* 内部核心光 */}
          <div
            style={{
              position: 'absolute',
              inset: '15%',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(0, 212, 255, 0.15) 0%, transparent 70%)',
              animation: shouldAnimate ? 'sphereCorePulse 2s ease-in-out infinite' : 'none',
            }}
          />

          {/* 主高光 - 玻璃质感 */}
          <div
            style={{
              position: 'absolute',
              top: '8%',
              left: '15%',
              width: '45%',
              height: '32%',
              borderRadius: '50%',
              background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.15) 35%, transparent 65%)',
              filter: 'blur(0.3px)',
            }}
          />
          {/* 锐利小高光 */}
          <div
            style={{
              position: 'absolute',
              top: '10%',
              left: '22%',
              width: '18%',
              height: '12%',
              borderRadius: '50%',
              background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.3) 40%, transparent 70%)',
              filter: 'blur(0px)',
            }}
          />
          {/* 底部环境反光 */}
          <div
            style={{
              position: 'absolute',
              bottom: '8%',
              right: '12%',
              width: '55%',
              height: '45%',
              borderRadius: '50%',
              background: 'radial-gradient(ellipse at center, rgba(0, 212, 255, 0.12) 0%, rgba(147, 51, 234, 0.06) 40%, transparent 70%)',
              filter: 'blur(2px)',
            }}
          />
          {/* 左下侧微光 */}
          <div
            style={{
              position: 'absolute',
              bottom: '18%',
              left: '10%',
              width: '30%',
              height: '25%',
              borderRadius: '50%',
              background: 'radial-gradient(ellipse at center, rgba(59, 130, 246, 0.1) 0%, transparent 60%)',
              filter: 'blur(1.5px)',
            }}
          />

          {/* 球体边缘 - 精致描边 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: `
                inset 0 0 8px rgba(0, 212, 255, 0.08),
                inset 0 1px 1px rgba(255, 255, 255, 0.15)
              `,
            }}
          />
        </div>
      </div>
    </>
  );
};

const styleId = 'flowing-sphere-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes sphereRotate1 {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes sphereRotate2 {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes sphereRotate3 {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes sphereRingPulse {
      0%, 100% {
        transform: scale(1);
        opacity: 0.3;
        border-color: rgba(0, 212, 255, 0.15);
      }
      50% {
        transform: scale(1.2);
        opacity: 0.7;
        border-color: rgba(147, 51, 234, 0.35);
      }
    }
    @keyframes sphereGlowBreathe {
      0%, 100% {
        opacity: 0.5;
        transform: scale(0.9);
      }
      50% {
        opacity: 1;
        transform: scale(1.25);
      }
    }
    @keyframes sphereCorePulse {
      0%, 100% {
        opacity: 0.4;
        transform: scale(0.85);
      }
      50% {
        opacity: 0.9;
        transform: scale(1.1);
      }
    }
  `;
  document.head.appendChild(style);
}
